/**
 * useMobileSsoConfig Hooks
 *
 * React Query hooks for the per-client mobile SSO / IdP connection config.
 */

import { usersQueryClient } from '@/lib/api/ts-rest-client';
import { queryKeys } from '@/lib/query-client';
import type { UpsertMobileSsoConfigBody } from '@skewb-works/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function useMobileSsoConfigs() {
  const queryKey = queryKeys.users.mobileSsoConfigs();
  const query = usersQueryClient.listMobileSsoConfigs.useQuery(queryKey, {}, { queryKey });
  return {
    ...query,
    data: query.data?.status === 200 ? query.data.body : undefined,
  };
}

export function useUpsertMobileSsoConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      earClientId,
      body,
    }: {
      earClientId: number;
      body: UpsertMobileSsoConfigBody;
    }) => {
      const result = await usersQueryClient.upsertMobileSsoConfig.mutation({
        params: { earClientId: String(earClientId) },
        body,
      });
      if (result.status !== 200) {
        throw new Error((result.body as any)?.error?.message || 'Failed to save SSO config');
      }
      return result.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.mobileSsoConfigs() });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.mobileSsoAvailability() });
    },
    meta: {
      successTitle: 'SSO Config Saved',
      successMessage: 'Mobile SSO configuration updated',
      errorTitle: 'Failed to Save SSO Config',
    },
  });
}
